import { useQuery } from '@tanstack/react-query'

import { getSupplierPolicy } from './api'
import { FormSkeleton } from './form-skeleton'
import { NoneButton } from './none-button'
import { NoneSelectedRow } from './none-selected-row'
import { POLICIES_FIELDS, PoliciesFormProvider } from './policies-provider'
import { PrimaryButton } from './primary-button'
import { StepFooter } from './step-footer'
import { TogglePill } from './toggle-pill'
import { usePoliciesForm } from './use-policies-form'

export function SupplierPoliciesForm({
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  onSuccess: () => void
  submitLabel?: string
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['supplier-policies'],
    queryFn: ({ signal }) => getSupplierPolicy(signal)
  })

  if (isLoading) return <FormSkeleton />
  return (
    <PoliciesFormProvider initial={data?.policy ?? null} onSuccess={onSuccess}>
      <FormBody submitLabel={submitLabel} />
    </PoliciesFormProvider>
  )
}

function FormBody({ submitLabel }: { submitLabel: string }) {
  const {
    state,
    dispatch,
    isSubmitting,
    selectedCount,
    canSubmit,
    handleSubmit
  } = usePoliciesForm()

  return (
    <form onSubmit={handleSubmit} className='flex w-full flex-col gap-4'>
      <section className='rounded-2xl border border-border bg-page p-6'>
        {state.noneSelected ? (
          <NoneSelectedRow
            option={{
              label: 'None of the above',
              description:
                'My business does not maintain any of these policies.'
            }}
            onClear={() => dispatch({ type: 'clearNone' })}
          />
        ) : (
          <>
            <ul className='flex flex-col gap-3'>
              {POLICIES_FIELDS.map(field => (
                <li key={field.key}>
                  <label
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
                      state[field.key]
                        ? 'border-accent bg-accent-light/40'
                        : 'border-border bg-page hover:border-accent/40 hover:bg-cream/40'
                    }`}
                  >
                    <input
                      type='checkbox'
                      checked={state[field.key]}
                      onChange={() =>
                        dispatch({ type: 'toggle', field: field.key })
                      }
                      className='mt-0.5 size-4 cursor-pointer rounded border-border accent-accent'
                    />
                    <div className='min-w-0 flex-1'>
                      <p className='text-[14px] font-medium text-ink'>
                        {field.label}
                      </p>
                      <p className='mt-0.5 text-[12px] text-ink-soft'>
                        {field.description}
                      </p>
                    </div>
                  </label>
                </li>
              ))}
            </ul>
            <NoneButton
              label='None of the above. My business does not maintain any of these policies.'
              onClick={() => dispatch({ type: 'selectNone' })}
            />
          </>
        )}
      </section>

      <section className='rounded-2xl border border-border bg-page p-6'>
        <p className='text-[14px] font-medium text-ink'>
          Are you a Licensed Exporter?
        </p>
        <p className='mt-0.5 text-[12px] text-ink-soft'>
          Your business holds a license authorizing it to export controlled
          goods or technology.
        </p>
        <div className='mt-3 flex items-center gap-2'>
          <TogglePill
            label='Yes'
            selected={state.licensedExporter}
            onClick={() =>
              dispatch({ type: 'setLicensedExporter', value: true })
            }
          />
          <TogglePill
            label='No'
            selected={!state.licensedExporter}
            onClick={() =>
              dispatch({ type: 'setLicensedExporter', value: false })
            }
          />
        </div>
      </section>

      {state.submitError && (
        <p className='text-sm text-red-600'>{state.submitError}</p>
      )}

      <StepFooter
        hint={
          state.noneSelected
            ? 'None selected'
            : `${selectedCount} of ${POLICIES_FIELDS.length} selected`
        }
      >
        <PrimaryButton type='submit' disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? 'Saving…' : submitLabel}
        </PrimaryButton>
      </StepFooter>
    </form>
  )
}
